import { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Bini } from "./Bini";
import { t } from "../i18n";

let apiPromise;
const loadApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      window.onYouTubeIframeAPIReady = () => resolve(window.YT);
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    });
  }
  return apiPromise;
};

// Privacy-enhanced player; covers the end screen to hide suggestions
export const YouTubePlayer = ({ videoId, autoplay = false, dark = false, testId = "youtube-player", playerRef }) => {
  const host = useRef(null);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    let player;
    let cancelled = false;
    loadApi().then((YT) => {
      if (cancelled || !host.current) return;
      player = new YT.Player(host.current, {
        host: "https://www.youtube-nocookie.com",
        videoId,
        playerVars: { autoplay: autoplay ? 1 : 0, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3, fs: 1 },
        events: {
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) setEnded(true);
            if (e.data === YT.PlayerState.PLAYING) setEnded(false);
          },
        },
      });
      if (playerRef) playerRef.current = player;
    });
    return () => {
      cancelled = true;
      if (player?.destroy) player.destroy();
      if (playerRef) playerRef.current = null;
    };
  }, [videoId, autoplay, playerRef]);

  const replay = () => {
    const p = playerRef?.current;
    setEnded(false);
    if (p?.seekTo) { p.seekTo(0); p.playVideo(); }
  };

  return (
    <div data-testid={testId} className={`relative aspect-video w-full overflow-hidden rounded-[28px] border-4 ${dark ? "border-[#1C2541] bg-black" : "border-white bg-[#1E2A5A]"} shadow-xl`}>
      <div className="absolute inset-0 [&>iframe]:h-full [&>iframe]:w-full"><div ref={host} /></div>
      {ended && (
        <div data-testid="player-ended-overlay" className={`absolute inset-0 flex flex-col items-center justify-center gap-4 ${dark ? "bg-[#0B132B]" : "bg-[#38B6FF]"}`}>
          <Bini size={90} mood={dark ? "sleep" : "happy"} />
          {!dark && (
            <>
              <p className="text-xl font-bold text-white">{t("songs.ended")}</p>
              <button type="button" onClick={replay} data-testid="player-replay-btn" className="btn-3d is-ink" style={{ "--c": "#FFD21F" }}>
                <RotateCcw className="w-6 h-6" strokeWidth={3} /> {t("songs.replay")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
